// Helper function to parse JSON string or return array
const parseJsonOrArray = (value) => {
    if (!value) return []
    if (typeof value === 'string') {
        try {
            const parsed = JSON.parse(value)
            return Array.isArray(parsed) ? parsed : []
        } catch {
            return []
        }
    }
    return Array.isArray(value) ? value : []
}

const createDateFromTimeString = (timeString) => {
    if (!timeString) return null
    const [hours, minutes, seconds] = timeString.split(':').map(Number)
    const now = new Date()
    now.setHours(hours, minutes, seconds, 0)
    return now
}

const buildReminderSchedulesFromFlat = (rawMediatorSettings) => {
    const schedules = {}

    Object.entries(rawMediatorSettings || {}).forEach(([key, value]) => {
        const match = key.match(/^(onboarding|brief|payment)_reminder_(\d+)_days$/)
        if (match) {
            const categoryKey = match[1]
            const index = match[2]

            if (!schedules[categoryKey]) {
                schedules[categoryKey] = {}
            }

            schedules[categoryKey][`reminder_${index}_days`] = value ?? null
        }
    })

    return schedules
}

const processMediatorRelationships = (accountData) => {
    const mediatorRelationships = accountData?.mediator_relationships || []

    return mediatorRelationships
        .filter(
            (rel) =>
                rel.parent_mediator_id === accountData.mediator_id ||
                rel.parent_mediator_id?.mediator_id === accountData.mediator_id
        )
        .map((rel) => ({
            ...rel.child_mediator_id,
            mediator_id: rel.child_mediator_id?.mediator_id,
            user_id: rel.child_mediator_id?.user_id,
            first_name: rel.child_mediator_id?.first_name,
            last_name: rel.child_mediator_id?.last_name,
            email: rel.child_mediator_id?.email,
        }))
}

const processReminderSchedules = (rawMediatorSettings) => {
    let reminderSchedules = rawMediatorSettings?.reminder_schedules || {}

    if (
        !reminderSchedules ||
        Object.keys(reminderSchedules || {}).length === 0
    ) {
        reminderSchedules = buildReminderSchedulesFromFlat(rawMediatorSettings)
    }

    return reminderSchedules
}

const buildSafeMediatorSettings = (rawMediatorSettings) => {
    const {
        no_of_brief_reminders,
        reminder_schedules: _ignoredReminderSchedules,
        ...restMediatorSettings
    } = rawMediatorSettings || {}

    return Object.fromEntries(
        Object.entries(restMediatorSettings).filter(
            ([key]) =>
                !/^(onboarding|brief|payment)_reminder_\d+_days$/.test(key)
        )
    )
}

export const processMediatorAccountData = (accountData) => {
    if (!accountData) {
        return {}
    }

    const childMediators = processMediatorRelationships(accountData)
    const rawMediatorSettings = accountData?.mediator_settings || {}
    const reminderSchedules = processReminderSchedules(rawMediatorSettings)
    const safeMediatorSettings = buildSafeMediatorSettings(rawMediatorSettings)

    const additionalInfo =
        Array.isArray(accountData?.mediator_additional_info) &&
            accountData?.mediator_additional_info?.length > 0
            ? accountData?.mediator_additional_info[0]
            : accountData?.mediator_additional_info || null

    return {
        profile: {
            id: accountData.mediator_id,
            name: `${accountData.first_name} ${accountData.last_name}`,
            first_name: accountData.first_name,
            last_name: accountData.last_name,
            email: accountData.email,
            bio: accountData.bio,
        },
        mediationRates: {
            halfday: accountData.half_day_rate,
            fullday: accountData.full_day_rate,
            processing_fee_to_client: accountData.processing_fee_to_client,
            hourly_rate: accountData.hourly_rate,
            deposit: accountData.deposit,
            pricing_type: accountData.pricing_type,
            car_hourly_rate: accountData.car_hourly_rate,
        },
        invoiceMemo: {
            policy: accountData.cancellation_policy,
            additional: accountData.additional_information,
            invoice_footer: accountData?.invoice_footer,
        },
        reminderDays: {
            payment_reminder_days: accountData?.payment_reminder_days,
            brief_reminder_days: accountData?.brief_reminder_days,
        },
        mediationTime: {
            morning: createDateFromTimeString(accountData?.morning_time),
            afternoon: createDateFromTimeString(accountData?.afternoon_time),
            fullday: createDateFromTimeString(accountData?.fullday_time),
            morning_time: accountData?.morning_time,
            afternoon_time: accountData?.afternoon_time,
            fullday_time: accountData?.fullday_time,
            timezone: accountData?.timezone,
        },
        mediationEmailCc: {
            email_cc: accountData?.email_cc || [],
            email_cc_mediators: accountData?.email_cc_mediators || [],
        },
        adminFee: {
            custom_percentage_for_coupon:
                accountData?.custom_percentage_for_coupon,
            admin_fee_pay_by: accountData?.admin_fee || 'client',
            admin_fee_amount: accountData?.fee_amount,
            admin_fee_type: accountData?.fee_type,
        },
        mediatorSettings:
            accountData?.mediator_settings !== null
                ? {
                    ...safeMediatorSettings,
                    morning: createDateFromTimeString(
                        accountData?.mediator_settings?.gsc_morning_time
                    ),
                    afternoon: createDateFromTimeString(
                        accountData?.mediator_settings?.gsc_afternoon_time
                    ),
                    fullday: createDateFromTimeString(
                        accountData?.mediator_settings?.gsc_fullday_time
                    ),
                    ai_internet_access_mode: accountData?.mediator_settings?.ai_internet_access_mode || 'case_document_only', // Note: Using string literal here as this is data processing, not business logic
                    reminders: {
                        onboarding: reminderSchedules?.onboarding || {},
                        brief: reminderSchedules?.brief || {},
                        payment: reminderSchedules?.payment || {},
                    },
                }
                : null,
        mediator_files: accountData?.mediator_upload_files || [],
        timezone: accountData?.timezone,
        cancellation_policy: accountData?.cancellation_policy,
        invoice_footer: accountData?.invoice_footer,
        additional_information: accountData?.additional_information,
        stripeId: accountData?.stripe_sessions?.stripe_id,
        stripeAddress: accountData?.stripe_sessions?.stripe_address,
        stripeLogo: accountData?.stripe_sessions?.stripe_logo,
        mediator_id: accountData?.mediator_id,
        user_id: accountData?.user_id,
        googleSession: accountData?.google_sessions?.[0] ?? null,
        outlookSession: accountData?.outlook_sessions?.[0] ?? null,
        is_odr_mediator: accountData?.is_odr_mediator || false,
        coupons: accountData?.coupons || [],
        is_corporate_code_visible: accountData?.is_corporate_code_visible,
        is_additional_case_names_allow:
            accountData?.is_additional_case_names_allow,
        invoice_allocation_party: accountData?.invoice_allocation_party,
        proceed_conflict_allowed: accountData?.proceed_conflict_allowed,
        stripe_session: accountData?.stripe_sessions,

        zoom: accountData?.zoom,
        is_car_portal: accountData?.is_car_portal || false,

        mediator_relationships: childMediators,


        additional_info: additionalInfo
            ? {
                practice_areas: parseJsonOrArray(
                    additionalInfo?.practice_areas
                ),
                languages: parseJsonOrArray(additionalInfo.languages),
                education: parseJsonOrArray(additionalInfo.education),
                bar_admissions: parseJsonOrArray(
                    additionalInfo.bar_admissions
                ),
                awards: parseJsonOrArray(additionalInfo.awards),
                service_area: additionalInfo.service_area || null,
            }
            : null,
    }
}

